/**
 * Privacy, Terms, Refund and Contact — on both sign-in pages, the sign-up
 * page, and inside the portal for every role.
 *
 * Ported from the Flutter app's `widgets/policy_links.dart` so the two
 * clients offer the same four links to the same four places.
 *
 * *** THE PAGES BELONG TO THE INSTITUTE, NOT TO US ***
 * All four open the client's own merchant page at `/institute/<slug>`:
 * Privacy at `#privacy`, Terms the page itself (its sections ARE the terms),
 * Refund at `#refund`, Contact at `#contact`. That page is what the payment
 * gateway and the app stores were pointed at, so linking to it keeps one
 * source of truth. A student's agreement is with their institute, not with
 * the platform.
 *
 * *** NO /api/v1 ON THAT PATH ***
 * `merchant_page` is mounted at the application ROOT, deliberately, because
 * a bank reviewer opens it in a browser. `/api/v1/institute/...` answers 401.
 *
 * *** THE FALLBACK HAS ONLY TWO LINKS ***
 * With no slug — the platform's admin door at `/login` — there is no
 * institute page to point at, so Privacy and Terms fall back to the
 * marketing site and Refund and Contact are NOT shown. An institute's refund
 * policy is the institute's; inventing a platform one would be a false
 * statement about somebody's money.
 */

import styles from './PolicyLinks.module.css'

export interface PolicyLink {
  label: string
  href: string
}

/**
 * The links this page can offer.
 *
 * `slug` is the institute; omit it only where there genuinely is none.
 */
export function policyLinks(slug?: string | null): PolicyLink[] {
  if (!slug) {
    /*
      The marketing site, served by nginx from a different directory. Only
      these two exist there — see `Mentor_SITE/`.
    */
    return [
      { label: 'Privacy Policy', href: '/privacy' },
      { label: 'Terms of Service', href: '/terms' },
    ]
  }

  const page = `/institute/${encodeURIComponent(slug)}`
  return [
    { label: 'Privacy Policy', href: `${page}#privacy` },
    { label: 'Terms of Service', href: page },
    { label: 'Refund Policy', href: `${page}#refund` },
    { label: 'Contact Us', href: `${page}#contact` },
  ]
}

/**
 * A centred row of small text links, for the sign-in and sign-up cards.
 *
 * *** REAL ANCHORS, NOT ROUTER LINKS ***
 * `/institute/<slug>` is served by the API, not by this single-page app, so
 * a `<Link>` would push a route React has no match for and land on the
 * catch-all. `target="_blank"` keeps a half-filled sign-up form intact, and
 * `rel="noopener"` because the opened page is outside the SPA.
 */
export function PolicyLinksFooter({ slug }: { slug?: string | null }) {
  const links = policyLinks(slug)
  return (
    <nav className={styles.footer} aria-label="Policies">
      {links.map((link) => (
        <a
          key={link.label}
          className={styles.footerLink}
          href={link.href}
          target="_blank"
          rel="noopener noreferrer"
        >
          {link.label}
        </a>
      ))}
    </nav>
  )
}

/**
 * The same four links as a labelled group, for inside the portal.
 *
 * Shown to students, mentors AND admins: an admin signs the same agreement
 * and is likelier than anyone to need the contact details.
 */
export function PolicyLinksGroup({ slug }: { slug?: string | null }) {
  const links = policyLinks(slug)
  return (
    <section className={styles.group} aria-label="Policies and support">
      <h2 className={styles.groupTitle}>Policies &amp; support</h2>
      <ul className={styles.list}>
        {links.map((link) => (
          <li key={link.label}>
            <a
              className={styles.tile}
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span>{link.label}</span>
              {/* Says the link leaves the portal, so the new tab is not a
                  surprise. */}
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                className={styles.tileIcon}
              >
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                <path d="M15 3h6v6M10 14 21 3" />
              </svg>
            </a>
          </li>
        ))}
      </ul>
    </section>
  )
}
